import type ImageType from "@travily/types/shared/Image";
import { Avatar, type AvatarColor, type AvatarSize } from "@ui/components/Avatar";
import { tv } from "@ui/lib/tv";
import { MemberCheckBadge } from "./MemberCheckBadge";

const selectableAvatar = tv({
  slots: {
    root: "relative shrink-0",
    // "Green ring → white gap → avatar" when selected, a plain white ring otherwise.
    // Uses `ring` + `ring-offset` (Tailwind v4 composes them with the drop shadow)
    // so the green ring is a solid 2px, not a hairline lost behind the avatar's
    // own 1px outline.
    avatar: "shadow-chip",
    badge: "absolute -right-1 -top-1",
  },
  variants: {
    isSelected: {
      true: { avatar: "ring-2 ring-green-500 ring-offset-2 ring-offset-white" },
      false: { avatar: "ring-2 ring-white" },
    },
  },
  defaultVariants: { isSelected: false },
});

export interface SelectableAvatarProps {
  isSelected: boolean;
  /** Optional member photo; falls back to the colored initial. */
  avatar?: ImageType | null;
  /** Character(s) displayed in the avatar when there is no photo. */
  initial?: string | null;
  /** Avatar background colour for the initial fallback. */
  color?: AvatarColor;
  size?: AvatarSize;
  /** Extra classes for the avatar circle (typically sizing, e.g. `size-11`). */
  className?: string;
  /** Extra classes for the check badge (typically sizing; defaults to its natural 27px). */
  badgeClassName?: string;
}

/**
 * The "selected member" avatar pattern: a circular member avatar wearing a
 * white ring, swapped for a green ring with a white gap plus a check badge
 * when selected. Shared by the SearchBar Who popover and the trip family
 * picker so selection renders identically across surfaces.
 */
export function SelectableAvatar({
  isSelected,
  avatar,
  initial,
  color,
  size,
  className,
  badgeClassName,
}: SelectableAvatarProps) {
  const styles = selectableAvatar({ isSelected });

  return (
    <div className={styles.root()}>
      <Avatar.Root
        className={styles.avatar({ className })}
        color={color}
        image={avatar}
        initial={initial}
        shape="circle"
        size={size}
      />
      {isSelected && <MemberCheckBadge className={styles.badge({ className: badgeClassName })} />}
    </div>
  );
}
